import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, FileText, Lightbulb, ListChecks,
  Table, PenTool, Download
} from 'lucide-react';

const InlineCode = ({ children }) => (
  <code className="bg-gray-100 text-sm font-mono px-1.5 py-0.5 rounded text-gray-800">
    {children}
  </code>
);

const SectionHeader = ({ icon: Icon, title, id }) => (
  <div id={id} className="flex items-center gap-3 mb-4">
    <div className="p-2 bg-blue-50 rounded-lg">
      <Icon className="h-5 w-5 text-blue-600" />
    </div>
    <h2 className="text-xl font-bold text-gray-900">{title}</h2>
  </div>
);

const FieldTable = ({ title, fields }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">{title}</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 font-semibold text-gray-700">Field</th>
            <th className="text-left py-2 px-3 font-semibold text-gray-700">What It Fills In</th>
            <th className="text-left py-2 px-3 font-semibold text-gray-700">Example</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, idx) => (
            <tr key={field.code} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className="py-2 px-3">
                <InlineCode>{field.code}</InlineCode>
              </td>
              <td className="py-2 px-3 text-gray-600">{field.description}</td>
              <td className="py-2 px-3 text-gray-500 italic">{field.example}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CLIENT_FIELDS = [
  { code: '{{client_first_name}}', description: "Client's first name", example: 'Jane' },
  { code: '{{client_last_name}}', description: "Client's last name", example: 'Smith' },
  { code: '{{client_full_name}}', description: "Client's full name", example: 'Jane Smith' },
  { code: '{{client_email}}', description: "Client's email", example: 'jane@example.com' },
  { code: '{{client_phone}}', description: "Client's phone", example: '(555) 123-4567' },
  { code: '{{client_address_line1}}', description: 'Street address', example: '123 Main St' },
  { code: '{{client_address_line2}}', description: 'Unit/Suite', example: 'Suite 200' },
  { code: '{{client_city}}', description: 'City', example: 'Denver' },
  { code: '{{client_state}}', description: 'State', example: 'CO' },
  { code: '{{client_postal_code}}', description: 'ZIP code', example: '80202' },
  { code: '{{client_full_address}}', description: 'Full formatted address', example: '123 Main St, Suite 200, Denver, CO 80202' },
];

const CASE_FIELDS = [
  { code: '{{case_number}}', description: 'Case number', example: '2026-FAM-001' },
  { code: '{{case_title}}', description: 'Case title', example: 'Smith v. Smith' },
  { code: '{{case_type}}', description: 'Type of case', example: 'Family Law' },
  { code: '{{case_status}}', description: 'Current status', example: 'Active' },
  { code: '{{case_opened_date}}', description: 'Date case was opened', example: '03/23/2026' },
  { code: '{{case_description}}', description: 'Case description', example: 'Full text' },
  { code: '{{case_billing_type}}', description: 'Billing arrangement', example: 'Hourly' },
];

const ATTORNEY_FIELDS = [
  { code: '{{attorney_first_name}}', description: 'Assigned attorney first name', example: 'John' },
  { code: '{{attorney_last_name}}', description: 'Assigned attorney last name', example: 'Doe' },
  { code: '{{attorney_full_name}}', description: 'Assigned attorney full name', example: 'John Doe' },
  { code: '{{attorney_email}}', description: 'Attorney email', example: 'john@firm.com' },
  { code: '{{attorney_phone}}', description: 'Attorney phone', example: '(555) 987-6543' },
];

const FIRM_FIELDS = [
  { code: '{{firm_name}}', description: 'Firm name', example: 'Doe & Associates' },
  { code: '{{firm_address}}', description: 'Firm address', example: '456 Law Ave, Denver, CO 80202' },
  { code: '{{firm_phone}}', description: 'Firm phone', example: '(555) 000-1111' },
  { code: '{{firm_email}}', description: 'Firm email', example: 'info@firm.com' },
  { code: '{{firm_website}}', description: 'Firm website', example: 'www.firm.com' },
];

const DATE_FIELDS = [
  { code: '{{current_date}}', description: "Today's date", example: '03/23/2026' },
  { code: '{{current_date_long}}', description: "Today's date (long format)", example: 'March 23, 2026' },
  { code: '{{current_year}}', description: 'Current year', example: '2026' },
];

const EXAMPLE_TEMPLATE = `{{firm_name}}
{{firm_address}}
{{firm_phone}}

{{current_date_long}}

RE: {{case_title}} (Case No. {{case_number}})

Dear {{client_full_name}},

This letter is to confirm that our firm, {{firm_name}}, is representing you
in the above-referenced matter. Your case has been assigned to {{attorney_full_name}},
who can be reached at {{attorney_email}} or {{attorney_phone}}.

Please do not hesitate to contact us with any questions.

Sincerely,

{{attorney_full_name}}
{{firm_name}}`;

const DocumentTemplateHelp = () => {
  return (
    <div className="max-w-4xl mx-auto">
      {/* Back link */}
      <Link
        to="/documents"
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Templates
      </Link>

      {/* Page title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-600" />
          Document Templates Help
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Learn how to create and use document templates with auto-fill fields
        </p>
      </div>

      {/* Section 1: What Are Document Templates? */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <SectionHeader icon={BookOpen} title="What Are Document Templates?" id="overview" />
        <p className="text-gray-600 leading-relaxed">
          Document templates let you create reusable documents that automatically fill in with
          data from your cases, clients, attorneys, and firm. Instead of manually typing names,
          addresses, case numbers, and dates every time, you write the template once using
          placeholder fields like <InlineCode>{'{{client_full_name}}'}</InlineCode> and
          the system replaces them with the real data when you generate a document.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          Templates use the <InlineCode>{'{{placeholder}}'}</InlineCode> syntax &mdash; double
          curly braces around a field name &mdash; for fields that get replaced automatically
          when generating a document.
        </p>
      </div>

      {/* Section 2: Creating a Template */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <SectionHeader icon={PenTool} title="Creating a Template" id="creating" />
        <ol className="space-y-3 text-gray-600">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">1</span>
            <span>Go to <strong>Doc Templates</strong> from the navigation menu.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">2</span>
            <span>Click the <strong>&quot;New Template&quot;</strong> button.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">3</span>
            <span>Fill in the template <strong>name</strong> (required), <strong>category</strong>, and <strong>practice area</strong>.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">4</span>
            <span>Write your template content in the text area.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">5</span>
            <span>
              Use the <strong>placeholder palette</strong> on the right side to insert auto-fill
              fields &mdash; click any field to insert it at your cursor position.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">6</span>
            <span>Click <strong>&quot;Save&quot;</strong>.</span>
          </li>
        </ol>
      </div>

      {/* Section 3: Available Auto-Fill Fields */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <SectionHeader icon={Table} title="Available Auto-Fill Fields" id="fields" />
        <p className="text-gray-600 mb-6">
          There are 32 auto-fill fields available, organized into five groups.
          Click any field in the placeholder palette when editing a template to insert it.
        </p>

        <FieldTable title="Client Fields" fields={CLIENT_FIELDS} />
        <FieldTable title="Case Fields" fields={CASE_FIELDS} />
        <FieldTable title="Attorney Fields" fields={ATTORNEY_FIELDS} />
        <FieldTable title="Firm Fields" fields={FIRM_FIELDS} />
        <FieldTable title="Date Fields" fields={DATE_FIELDS} />
      </div>

      {/* Section 4: Generating a Document */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <SectionHeader icon={Download} title="Generating a Document" id="generating" />
        <ol className="space-y-3 text-gray-600">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">1</span>
            <span>Find your template on the <strong>Doc Templates</strong> page.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">2</span>
            <span>Click the green <strong>&quot;Generate&quot;</strong> button on the template card.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">3</span>
            <span>Search for and select the <strong>case</strong> to pull data from.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">4</span>
            <span>Choose output format: <strong>Word (DOCX)</strong> or <strong>PDF</strong>.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">5</span>
            <span>Optionally click <strong>&quot;Preview&quot;</strong> to see the filled-in document before saving.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">6</span>
            <span>
              Click <strong>&quot;Generate &amp; Save to Case&quot;</strong> &mdash; the document will
              be saved directly to the case&apos;s document folder.
            </span>
          </li>
        </ol>
      </div>

      {/* Section 5: Example Template */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <SectionHeader icon={FileText} title="Example Template" id="example" />
        <p className="text-gray-600 mb-4">
          Here is a sample engagement letter template showing how placeholders are used:
        </p>
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap leading-relaxed">
          {EXAMPLE_TEMPLATE}
        </pre>
      </div>

      {/* Section 6: Tips */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <SectionHeader icon={Lightbulb} title="Tips" id="tips" />
        <ul className="space-y-3 text-gray-600">
          <li className="flex gap-3">
            <ListChecks className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <span>
              If a field has no data for a case, it will appear as the raw placeholder
              (e.g., <InlineCode>{'{{client_phone}}'}</InlineCode>) in the generated
              document &mdash; make sure client and case records are complete.
            </span>
          </li>
          <li className="flex gap-3">
            <ListChecks className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <span>
              You can <strong>preview before generating</strong> to catch any missing fields.
            </span>
          </li>
          <li className="flex gap-3">
            <ListChecks className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <span>
              Templates are <strong>shared across your firm</strong> &mdash; any team member can use them.
            </span>
          </li>
          <li className="flex gap-3">
            <ListChecks className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <span>
              Use <strong>categories</strong> and <strong>practice areas</strong> to keep templates organized.
            </span>
          </li>
        </ul>
      </div>

      {/* Back link at bottom */}
      <div className="text-center mb-8">
        <Link
          to="/documents"
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Templates
        </Link>
      </div>
    </div>
  );
};

export default DocumentTemplateHelp;
